import React from 'react';
import allSvg from '../../assets/icons/map-layers/all.svg?raw';
import shipmentsSvg from '../../assets/icons/map-layers/shipments.svg?raw';
import weatherSvg from '../../assets/icons/map-layers/weather.svg?raw';
import growingSvg from '../../assets/icons/map-layers/growing.svg?raw';
import combineSvg from '../../assets/icons/map-layers/combine.svg?raw';
import arrivalsSvg from '../../assets/icons/map-layers/arrivals.svg?raw';
import { svgInner } from './svgInner';

/**
 * Map layer rail glyphs, drawn from the files exported with the design in
 * `src/assets/icons/map-layers/`. The file is the artwork — nothing here
 * restates its path data, so there is no second copy to drift.
 *
 * Imported as text and inlined rather than pointed at with an `<img>` or a CSS
 * mask, because the rail needs two things an image boundary does not carry:
 * `currentColor`, for the grey/green/white states, and control of the stroke
 * while the glyph grows 20px to 32px on hover. A masked image scales its
 * stroke with the box; real SVG lets the rail thin it instead.
 */
const STRIP = { stripStroke: true };

const ALL = svgInner(allSvg, STRIP);
const SHIPMENTS = svgInner(shipmentsSvg, STRIP);
const WEATHER = svgInner(weatherSvg, STRIP);
const GROWING = svgInner(growingSvg, STRIP);
const COMBINE = svgInner(combineSvg, STRIP);
const ARRIVALS = svgInner(arrivalsSvg, STRIP);

/**
 * Width/height are attributes, so a caller's `h-*`/`w-*` classes still win and
 * the rail can animate 20px to 32px on hover. `strokeWidth` is an attribute for
 * the same reason: the design does not scale the stroke with the glyph (1.5 at
 * 20px but 1.9 — not 2.4 — at 32px), so the rail overrides it in CSS per state.
 *
 * `dangerouslySetInnerHTML` carries build-time asset text, not anything that
 * reaches a user.
 */
const Svg = ({ size, className, markup, viewBox = '0 0 20 20', strokeWidth = 1.5, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    className={className}
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: markup }}
    {...props}
  />
);

/** All — stacked layers. */
export const AllIcon = ({ size = 20, className = '', ...props }) => (
  <Svg size={size} className={className} markup={ALL} {...props} />
);

/** Shipments — vessel on water. */
export const ShipmentsIcon = ({ size = 20, className = '', ...props }) => (
  <Svg size={size} className={className} markup={SHIPMENTS} {...props} />
);

/** Weather — cloud. */
export const WeatherIcon = ({ size = 20, className = '', ...props }) => (
  <Svg size={size} className={className} markup={WEATHER} {...props} />
);

/** Growing — sprout. */
export const GrowingIcon = ({ size = 20, className = '', ...props }) => (
  <Svg size={size} className={className} markup={GROWING} {...props} />
);

/** Combine — plus. */
export const CombineIcon = ({ size = 20, className = '', ...props }) => (
  <Svg size={size} className={className} markup={COMBINE} {...props} />
);

/**
 * Arrivals — package/box. Drawn on a 16px grid at stroke 1.2, unlike the 20px
 * layer glyphs.
 *
 * The export bakes the green in as a literal; `stripStroke` drops it so the
 * caller's `currentColor` drives the colour, the same as every other glyph
 * here. Nothing renders this yet.
 */
export const ArrivalsIcon = ({ size = 16, className = '', ...props }) => (
  <Svg
    size={size}
    className={className}
    markup={ARRIVALS}
    viewBox="0 0 16 16"
    strokeWidth={1.2}
    {...props}
  />
);
